// src/refracted Dashboard/SpotifyDashboardSearch.js - FIXED VERSION
import React, { useState, useEffect, useRef } from 'react';
import { usePlayerContext } from '../../hooks/PlayerContext';
import logger from '../../services/logger';

const SpotifyDashboardSearch = () => {
  const { 
    state, 
    setPlaylist, 
    setCurrentTrackIndex, // Add this
    playTrack, // Use this instead of handleTrackPlay
    addTrackToPlaylist,
    normalizeTrackFormat
  } = usePlayerContext();
  
  const { accessToken } = state;

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('track');
  const timeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  
  // Handle search input changes with debounce
  useEffect(() => {
    if (searchTerm.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      performSearch();
    }, 500);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchTerm, activeTab, accessToken]);

  // Perform search using Spotify API
  const performSearch = async () => {
    if (!searchTerm.trim() || !accessToken) return;
    
    setLoading(true);
    logger.debug(`Searching for "${searchTerm}" (type: ${activeTab})`);
    
    try {
      const response = await fetch(
        `https://api.spotify.com/v1/search?q=${encodeURIComponent(searchTerm)}&type=${activeTab}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Process results based on search type
      let formattedResults = [];
      
      if (activeTab === 'track' && data.tracks?.items) {
        formattedResults = data.tracks.items.map(track => ({
          id: track.id,
          uri: track.uri,
          name: track.name,
          artists: track.artists.map(a => a.name).join(', '),
          album: track.album.name,
          images: track.album.images,
          duration_ms: track.duration_ms,
          type: 'track'
        }));
      } else if (activeTab === 'album' && data.albums?.items) {
        formattedResults = data.albums.items.map(album => ({
          id: album.id,
          uri: album.uri,
          name: album.name,
          artists: album.artists.map(a => a.name).join(', '),
          images: album.images,
          type: 'album'
        }));
      } else if (activeTab === 'artist' && data.artists?.items) {
        formattedResults = data.artists.items.map(artist => ({
          id: artist.id,
          uri: artist.uri,
          name: artist.name,
          images: artist.images,
          type: 'artist'
        }));
      }
      
      setSearchResults(formattedResults);
    } catch (error) {
      logger.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Use the correct function name and ensure proper track formatting
  const playTrackNow = (track) => {
    try {
      if (!track || typeof track !== 'object') {
        logger.error("Invalid track data:", track);
        return;
      }

      // Format the track properly using the context's normalize function
      const normalizedTrack = normalizeTrackFormat(track);

      if (!normalizedTrack) {
        logger.error("Failed to normalize track for playback");
        return;
      }

      logger.debug("Playing track now:", normalizedTrack);

      // Use playTrack from PlayerContext (this exists)
      playTrack(normalizedTrack);

    } catch (error) {
      logger.error("Error playing track now:", error);
    }
  };

  // FIXED: Improved track handling for next/queue functions
  const playTrackNext = (track) => {
    try {
      if (!track || typeof track !== 'object') {
        logger.error("Invalid track data:", track);
        return;
      }

      const normalizedTrack = normalizeTrackFormat(track);
      if (!normalizedTrack) {
        logger.error("Failed to normalize track");
        return;
      }

      logger.debug("Playing track next:", normalizedTrack);

      // Add track to position after current track
      addTrackToPlaylist(normalizedTrack, 'next');

    } catch (error) {
      logger.error("Error playing track next:", error);
    }
  };

  const addTrackToQueue = (track) => {
    try {
      if (!track || typeof track !== 'object') {
        logger.error("Invalid track data:", track);
        return;
      }

      const normalizedTrack = normalizeTrackFormat(track);
      if (!normalizedTrack) {
        logger.error("Failed to normalize track");
        return;
      }

      logger.debug("Adding to queue:", normalizedTrack);

      // Add track to end of playlist
      addTrackToPlaylist(normalizedTrack, 'end');

    } catch (error) {
      logger.error("Error adding track to queue:", error);
    }
  };
  
  // Format time (ms to MM:SS)
  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = ((ms % 60000) / 1000).toFixed(0);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <div className="content-section">
      <div className="section-header">
        <h2>Search Music</h2>
      </div>

      <div className="search-container">
        {/* Search input */}
        <div className="dashboard-search-input">
          <div className="search-icon">🔍</div>
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search for songs, artists, or albums..."
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="clear-search-button"
              onClick={() => setSearchTerm('')}
            >
              ✕
            </button>
          )}
        </div>
        
        {/* Search tabs */}
        <div className="search-tabs">
          <button
            className={`search-tab ${activeTab === 'track' ? 'active' : ''}`}
            onClick={() => setActiveTab('track')}
          >
            Tracks
          </button>
          <button
            className={`search-tab ${activeTab === 'album' ? 'active' : ''}`}
            onClick={() => setActiveTab('album')}
          >
            Albums
          </button>
          <button
            className={`search-tab ${activeTab === 'artist' ? 'active' : ''}`}
            onClick={() => setActiveTab('artist')}
          >
            Artists
          </button>
        </div>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="search-loading">
          <div className="loading-spinner"></div>
          <p>Searching Spotify...</p>
        </div>
      )}
      
      {/* No search term state */}
      {!loading && searchTerm.trim().length < 2 && (
        <div className="search-placeholder">
          <div className="search-icon-large">🎵</div>
          <p>Start typing to search for music</p>
        </div>
      )}
      
      {/* No results state */}
      {!loading && searchTerm.trim().length >= 2 && searchResults.length === 0 && (
        <div className="search-no-results">
          <p>No results found for "{searchTerm}"</p>
        </div>
      )}
      
      {/* Search results - Track view */}
      {activeTab === 'track' && searchResults.length > 0 && (
        <div className="search-results-grid">
          {searchResults.map((track, index) => (
            <div key={track.id} className="media-item track-item">
              <div className="media-cover-art">
                <img src={track.images?.[0]?.url || '/default-album.png'} alt={track.name} />
                <div className="media-play-overlay">
                  <button 
                    className="play-button"
                    onClick={() => playTrackNow(track)}
                  >▶</button>
                </div>
              </div>
              <div className="media-info">
                <div className="media-title">{track.name}</div>
                <div className="media-subtitle">{track.artists}</div>
                <div className="track-actions">
                  <button className="track-action-button">•••</button>
                  <div className="hover-menu">
                    <button onClick={() => playTrackNow(track)}>Play Now</button>
                    <button onClick={() => playTrackNext(track)}>Play Next</button>
                    <button onClick={() => addTrackToQueue(track)}>Add to Queue</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Search results - Albums view */}
      {activeTab === 'album' && searchResults.length > 0 && (
        <div className="search-results-grid">
          {searchResults.map(album => (
            <div key={album.id} className="media-item album-item">
              <div className="media-cover-art">
                <img src={album.images?.[0]?.url || '/default-album.png'} alt={album.name} />
                <div className="media-play-overlay">
                  <button className="play-button">▶</button>
                </div>
              </div>
              <div className="media-info">
                <div className="media-title">{album.name}</div>
                <div className="media-subtitle">{album.artists}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Search results - Artists view */}
      {activeTab === 'artist' && searchResults.length > 0 && (
        <div className="search-results-grid">
          {searchResults.map(artist => (
            <div key={artist.id} className="media-item artist-item">
              <div className="media-cover-art artist-cover">
                <img src={artist.images?.[0]?.url || '/default-artist.png'} alt={artist.name} />
                <div className="media-play-overlay">
                  <button className="play-button">▶</button>
                </div>
              </div>
              <div className="media-info">
                <div className="media-title">{artist.name}</div>
                <div className="media-subtitle">Artist</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SpotifyDashboardSearch;